import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React from 'react';
import { Button } from '@/components/ui/button';
import { ActionButton } from '@/store/videoFeatures/types';
import { sanitizeHTML } from '@/lib/security';

interface ActionButtonOverlayProps {
  actionButton?: ActionButton;
  controlsColor?: string;
  onClick?: () => void;
  isVideoEnded?: boolean;
  forceShow?: boolean;
}

export const ActionButtonOverlay: React.FC<ActionButtonOverlayProps> = ({
  actionButton,
  controlsColor = '#FFFFFF',
  onClick,
  isVideoEnded = false,
  forceShow = false
}) => {
  useLanguage();
  // If forceShow is true, show for preview purposes even if not enabled
  if (!actionButton || (!forceShow && !actionButton.enabled)) return null;

  // Only render if button type is "sobre-video"
  // Skip rendering for 'abaixo-video' and 'html' types (they render elsewhere)
  if (actionButton.buttonType === 'abaixo-video' || actionButton.buttonType === 'html') {
    return null;
  }

  // Also skip if buttonType is explicitly set but is not 'sobre-video'
  if (actionButton.buttonType && actionButton.buttonType !== 'sobre-video') {
    return null;
  }
  
  // Check if the button should be hidden at video end
  // If persistAfterEnd is not defined, default to showing (backward compatibility)
  if (isVideoEnded && actionButton.persistAfterEnd === false) {
    return null;
  }
  
  // Check for animation class
  const getAnimationClass = () => {
    switch(actionButton.animation) {
      case 'pulse':
        return 'animate-pulse';
      case 'bounce':
        return 'animate-bounce';
      case 'shake':
        return 'animate-shake';
      default:
        return '';
    }
  };
  
  // Get position styles based on position property, with fallback to center
  const getPositionStyles = () => {
    const position = actionButton.position || 'center';
    const offsetX = actionButton.horizontalOffset || 0;
    const offsetY = actionButton.verticalOffset || 0;
    
    switch(position) {
      case 'top-left':
        return { top: `calc(1rem + ${offsetY}px)`, left: `calc(1rem + ${offsetX}px)` };
      case 'top-center':
        return { top: `calc(1rem + ${offsetY}px)`, left: '50%', transform: `translateX(calc(-50% + ${offsetX}px))` };
      case 'top-right':
        return { top: `calc(1rem + ${offsetY}px)`, right: `calc(1rem - ${offsetX}px)` };
      case 'center-left':
        return { top: '50%', left: `calc(1rem + ${offsetX}px)`, transform: `translateY(calc(-50% + ${offsetY}px))` };
      case 'center':
        return { top: '50%', left: '50%', transform: `translate(calc(-50% + ${offsetX}px), calc(-50% + ${offsetY}px))` };
      case 'center-right':
        return { top: '50%', right: `calc(1rem - ${offsetX}px)`, transform: `translateY(calc(-50% + ${offsetY}px))` };
      case 'bottom-left':
        return { bottom: `calc(1rem - ${offsetY}px)`, left: `calc(1rem + ${offsetX}px)` };
      case 'bottom-center':
        return { bottom: `calc(1rem - ${offsetY}px)`, left: '50%', transform: `translateX(calc(-50% + ${offsetX}px))` };
      case 'bottom-right':
        return { bottom: `calc(1rem - ${offsetY}px)`, right: `calc(1rem - ${offsetX}px)` };
      default:
        return { top: '50%', left: '50%', transform: `translate(calc(-50% + ${offsetX}px), calc(-50% + ${offsetY}px))` };
    }
  };

  // Prioritize style.* (new format) over root properties (legacy)
  const bgColor = actionButton.style?.backgroundColor || actionButton.backgroundColor || actionButton.buttonColor || '#1D4FFF';
  const textColor = actionButton.style?.textColor || actionButton.textColor || '#FFFFFF';
  const borderRadius = actionButton.style?.borderRadius
    ? `${actionButton.style.borderRadius}px`
    : (actionButton.style?.rounded || actionButton.rounded) ? '9999px' : '0.375rem';

  // Get size-based styling
  const size = actionButton.size || 'medium';
  const getSizeClasses = () => {
    switch(size) {
      case 'small':
        return 'px-6 py-2 text-sm';
      case 'medium':
        return 'px-8 py-3 text-base';
      case 'large':
        return 'px-10 py-4 text-lg';
      default:
        return 'px-8 py-3 text-base';
    }
  };

  const buttonStyles = {
    backgroundColor: bgColor,
    color: textColor,
    fontSize: actionButton.fontSize || undefined,
    borderRadius,
  };

  // Handle button click
  const handleClick = () => {
    if (onClick) onClick();

    // Open URL if provided
    if (actionButton.url) {
      window.open(actionButton.url, '_blank');
    }
  };

  // Note: HTML custom buttons are rendered directly in VideoPlayer.tsx for proper positioning

  return (
    <div className={`absolute z-10`} style={getPositionStyles()}>
      <Button
        className={`${getAnimationClass()} ${getSizeClasses()}`}
        style={buttonStyles}
        onClick={handleClick}
      >
        {actionButton.text || translateInterfaceText("Clique Aqui")}
      </Button>
    </div>
  );
};
